import { zodResolver } from "@hookform/resolvers/zod";
import { Add } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

import { useCreateContractMutation } from "AdvisoryManagement/Api/Mutations/useCreateContractMutation";
import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";
import { BlFormAutocomplete } from "Forms/Components/BlFormAutocomplete";
import { ContractStatus } from "Infrastructure/Api/Api";
import { SidebarPanel } from "Shared/Sidebar";

const proposedSolutionSchema = z.object({
	categoryCode: z.string().min(1, "Vyberte kategorii produktu"),
	advisoryCaseId: z.string(),
	status: z.enum([ContractStatus.Draft, ContractStatus.InProgress]),
});

type ProposedSolutionFormData = z.infer<typeof proposedSolutionSchema>;

export const AddProposedSolutions = () => {
	const { advisoryCaseId } = useParams<{ advisoryCaseId: string }>();
	const [sidebarOpen, setSidebarOpen] = useState(false);

	const createProposedSolution = useCreateContractMutation();
	const productCategories = useProductCategoriesQuery();

	const form = useForm<ProposedSolutionFormData>({
		resolver: zodResolver(proposedSolutionSchema),
		defaultValues: {
			advisoryCaseId: advisoryCaseId ?? "",
			status: ContractStatus.Draft,
			categoryCode: "",
		},
	});

	const masterCategoriesList = useMemo(() => {
		if (!productCategories.data) return [];

		const categories = productCategories.data;

		const masterCategories = categories.filter((c) => c.level === 0);

		return masterCategories.map((master) => ({
			code: master.code,
			name: master.name,
		}));
	}, [productCategories.data]);

	const handleCreateProposedSolution = async (
		data: ProposedSolutionFormData,
	) => {
		try {
			await createProposedSolution.mutateAsync(data);

			toast.success("Řešení bylo úspěšně přidáno.");
		} catch (error) {
			toast.error("Nastala chyba při přidávání řešení.");
			console.error(error);
		} finally {
			form.reset();
			handleClose();
		}
	};

	const handleClose = () => {
		setSidebarOpen(false);
	};

	const handleOpen = () => {
		setSidebarOpen(true);
		form.reset();
	};

	return (
		<>
			<Button
				variant="text"
				color="primary"
				sx={{ marginLeft: "auto" }}
				endIcon={<Add />}
				onClick={handleOpen}
				component="span"
			>
				Přidat řešení
			</Button>
			<SidebarPanel
				open={sidebarOpen}
				onClose={handleClose}
				title="Přidat řešení"
			>
				<Stack spacing={3}>
					<Typography variant="body2" color="text.secondary">
						Vyberte typ řešení, které chcete navrhnout.
					</Typography>
					<form onSubmit={form.handleSubmit(handleCreateProposedSolution)}>
						<Stack spacing={2}>
							<BlFormAutocomplete
								control={form.control}
								name="categoryCode"
								label="Kategorie produktu"
								placeholder="Vyberte kategorii produktu"
								codeList={masterCategoriesList}
								errors={form.formState.errors}
								loading={productCategories.isLoading}
							/>
						</Stack>
					</form>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						<Button onClick={handleClose}>Zrušit</Button>
						<Button
							onClick={form.handleSubmit(handleCreateProposedSolution)}
							variant="contained"
						>
							Přidat
						</Button>
					</Box>
				</Stack>
			</SidebarPanel>
		</>
	);
};
